import {
  collection,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "src/firebase";
import {
  LearnerTopicsProgressAppModel,
  LearnerTrackAppModel,
  LearningTrackChapter,
  LearningTrackDetails,
} from "../types/learner-track.entity";

const LEVEL_ORDER = ["A1", "A2", "B1", "B2", "C1", "C2"];

const mapWithRef = <T>(id: string, docRef: DocumentReference, data: T) => ({
  ...data,
  id,
  docRef,
});

class LearnerTrackRepository {
  async getLearnerTracksByLearnerId(learnerId: string): Promise<LearnerTrackAppModel[]> {
    const tracksQuery = query(
      collection(db, "learner_tracks"),
      where("learnerId", "==", learnerId),
      orderBy("assignedAt", "desc")
    );

    const snapshot = await getDocs(tracksQuery);

    return snapshot.docs.map((trackDoc) =>
      mapWithRef(trackDoc.id, trackDoc.ref, trackDoc.data()) as LearnerTrackAppModel
    );
  }

  async getLearnerTopicsProgress(learnerTrackDocRef: DocumentReference): Promise<LearnerTopicsProgressAppModel[]> {
    const progressQuery = query(collection(learnerTrackDocRef, "learner_topics_progress"), orderBy("updatedAt", "desc"));
    const snapshot = await getDocs(progressQuery);

    return snapshot.docs.map((progressDoc) =>
      mapWithRef(progressDoc.id, progressDoc.ref, progressDoc.data()) as LearnerTopicsProgressAppModel
    );
  }

  async getLearningTrackById(trackId: string): Promise<LearningTrackDetails | null> {
    const trackRef = doc(db, "learning_tracks", trackId);
    const trackSnapshot = await getDoc(trackRef);

    if (!trackSnapshot.exists()) {
      return null;
    }

    const chaptersSnapshot = await getDocs(collection(trackRef, "chapters"));
    const chapters = await Promise.all(
      chaptersSnapshot.docs.map(async (chapterDoc) => {
        const topicsSnapshot = await getDocs(query(collection(chapterDoc.ref, "chapter_topics"), orderBy("order", "asc")));

        return {
          ...chapterDoc.data(),
          id: chapterDoc.id,
          docRef: chapterDoc.ref,
          topics: topicsSnapshot.docs.map((topicDoc) => mapWithRef(topicDoc.id, topicDoc.ref, topicDoc.data())),
        } as LearningTrackChapter;
      })
    );

    const sortedChapters = chapters.sort((a, b) => {
      const levelDiff = LEVEL_ORDER.indexOf(a.langLevel || "") - LEVEL_ORDER.indexOf(b.langLevel || "");
      if (levelDiff !== 0) {
        return levelDiff;
      }

      return (a.order || 0) - (b.order || 0);
    });

    return {
      ...trackSnapshot.data(),
      id: trackSnapshot.id,
      docRef: trackSnapshot.ref,
      chapters: sortedChapters,
    } as LearningTrackDetails;
  }
}

export const learnerTrackRepository = new LearnerTrackRepository();
